/**
 * Zebra SendFileToPrinter Service (Client-side)
 * Handles communication with backend API for Zebra printer integration
 * The backend manages API key and communicates with Zebra cloud service
 */

class ZebraPrintError extends Error {
    constructor(code, message, details = {}) {
        super(message);
        this.code = code;
        this.details = details;
        this.timestamp = new Date();
    }

    getUserFriendlyMessage() {
        const messages = {
            INVALID_SKUS: 'Please select at least one item to print.',
            NO_PRINTER_SELECTED: 'Please select a printer.',
            NETWORK_ERROR: 'Network error. Please check your connection and try again.',
            PRINTER_NOT_FOUND: 'Printer not found. Check the enrollment code and try again.',
            UNAUTHORIZED: 'Printer authorization failed. Check with your administrator.',
            RATE_LIMITED: 'Too many print requests. Please wait a moment and try again.',
            API_ERROR: 'Failed to send print job. Please try again.',
            UNKNOWN: 'An unexpected error occurred.',
        };

        return messages[this.code] || this.message;
    }
}

class ZebraPrintService {
    constructor() {
        this.apiUrl = import.meta.env.VITE_API_URL || '';
        this.selectedZebraPrinterStorageKey = 'selectedZebraPrinter';
    }

    getSelectedZebraPrinter() {
        try {
            const raw = localStorage.getItem(this.selectedZebraPrinterStorageKey);
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return null;

            const locationGid = typeof parsed.locationGid === 'string' ? parsed.locationGid : '';
            const deviceId = typeof parsed.deviceId === 'string' ? parsed.deviceId : '';

            if (!locationGid || !deviceId) return null;
            return { locationGid, deviceId };
        } catch {
            return null;
        }
    }

    setSelectedZebraPrinter(locationGid, deviceId) {
        try {
            if (!locationGid || typeof locationGid !== 'string') return;
            if (!deviceId || typeof deviceId !== 'string') return;

            localStorage.setItem(
                this.selectedZebraPrinterStorageKey,
                JSON.stringify({
                    locationGid,
                    deviceId,
                    savedAt: new Date().toISOString(),
                })
            );
        } catch {
            // Ignore storage errors (Safari private mode, disabled storage, etc.)
        }
    }

    clearSelectedZebraPrinter() {
        try {
            localStorage.removeItem(this.selectedZebraPrinterStorageKey);
        } catch {
            // Ignore
        }
    }

    /**
     * Send a print job to a Zebra printer.
     *
     * @param {Array<{sku: string, quantity: number}>} items - one entry per SKU, with
     *   how many labels that SKU needs. A flat SKU array is no longer accepted: the
     *   route printed one label per unique SKU and dropped quantity entirely.
     * @param {string} serialNumber
     * @param {Object} options
     */
    async printLabels(items, serialNumber, { skuType = 'sku', locationId = null, priceListId = null } = {}) {
        try {
            // Validate inputs
            if (!Array.isArray(items) || items.length === 0) {
                throw new ZebraPrintError('INVALID_SKUS', 'Items must be a non-empty array');
            }

            if (!serialNumber || typeof serialNumber !== 'string') {
                throw new ZebraPrintError('NO_PRINTER_SELECTED', 'Serial number is required');
            }

            console.log('[Zebra] Sending print job:', {
                skuCount: items.length,
                labelCount: items.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0),
                serialNumber: serialNumber.substring(0, 5) + '***',
            });

            // Call backend endpoint
            const response = await fetch(`${this.apiUrl}/product-labels/print-zebra`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    items,
                    serialNumber,
                    skuType,
                    locationId,
                    priceListId,
                }),
                signal: AbortSignal.timeout(30000),
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle specific error codes from backend
                const code = data.code || 'API_ERROR';
                throw new ZebraPrintError(code, data.message || 'Failed to send print job', {
                    statusCode: response.status,
                    serverResponse: data,
                });
            }

            console.log('[Zebra] Print job sent successfully:', data);
            return data.data;
        } catch (error) {
            if (error instanceof ZebraPrintError) {
                throw error;
            }

            if (error instanceof TypeError) {
                throw new ZebraPrintError('NETWORK_ERROR', 'Network error: ' + error.message);
            }

            throw new ZebraPrintError('UNKNOWN', error.message || 'Unknown error', {
                originalError: error,
            });
        }
    }
}

// Singleton instance
export const zebraPrintService = new ZebraPrintService();
export { ZebraPrintError };
